import { useState, useEffect } from 'react';
import { FileText, Plus, Download, Eye, Copy, XCircle, Search, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CreateTaxReceiptModal } from './CreateTaxReceiptModal';
import { TaxReceiptDetailModal } from './TaxReceiptDetailModal';

interface TaxReceipt {
  id: string;
  association_id: string;
  receipt_number: string;
  status: 'draft' | 'issued' | 'cancelled';
  donor_type: 'individual' | 'company';
  donor_name: string;
  donor_company_name: string | null;
  donation_date: string;
  donation_amount: number;
  donation_nature_amount: number;
  issue_date: string | null;
  pdf_url: string | null;
  created_at: string;
}

interface TaxReceiptsTabProps {
  associationId: string;
  associationName: string;
  userId: string;
}

export function TaxReceiptsTab({ associationId, associationName, userId }: TaxReceiptsTabProps) {
  const [receipts, setReceipts] = useState<TaxReceipt[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<TaxReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<TaxReceipt | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadReceipts();
  }, [associationId]);

  useEffect(() => {
    filterReceipts();
  }, [receipts, searchTerm, statusFilter]);

  async function loadReceipts() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tax_receipts')
        .select('*')
        .eq('association_id', associationId)
        .order('donation_date', { ascending: false });

      if (error) throw error;
      setReceipts(data || []);
    } catch (error) {
      console.error('Error loading tax receipts:', error);
    } finally {
      setLoading(false);
    }
  }

  function filterReceipts() {
    let filtered = [...receipts];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.receipt_number.toLowerCase().includes(term) ||
        r.donor_name.toLowerCase().includes(term) ||
        r.donor_company_name?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    setFilteredReceipts(filtered);
  }

  async function handleDownloadPDF(receiptId: string, pdfUrl: string | null) {
    if (!pdfUrl) {
      alert('PDF non disponible');
      return;
    }
    window.open(pdfUrl, '_blank');
  }

  async function handleDuplicate(receipt: TaxReceipt) {
    setSelectedReceipt({
      ...receipt,
      id: '',
      receipt_number: '',
      status: 'draft',
      issue_date: null,
      pdf_url: null,
      created_at: new Date().toISOString()
    });
    setShowCreateModal(true);
  }

  async function handleCancel(receiptId: string) {
    if (!confirm('Êtes-vous sûr de vouloir annuler ce reçu fiscal ?')) return;

    try {
      const { error } = await supabase
        .from('tax_receipts')
        .update({ status: 'cancelled' })
        .eq('id', receiptId);

      if (error) throw error;
      loadReceipts();
    } catch (error) {
      console.error('Error cancelling receipt:', error);
      alert('Erreur lors de l\'annulation du reçu');
    }
  }

  function getStatusBadge(status: string) {
    const badges = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-700' },
      issued: { label: 'Émis', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-700' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    );
  }

  const totalAmount = filteredReceipts.reduce((sum, r) => sum + Number(r.donation_amount) + Number(r.donation_nature_amount || 0), 0);
  const issuedCount = filteredReceipts.filter(r => r.status === 'issued').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reçus fiscaux</h2>
          <p className="text-sm text-gray-600 mt-1">
            Association : <span className="font-medium">{associationName}</span>
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedReceipt(null);
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouveau reçu fiscal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total des reçus</p>
              <p className="text-2xl font-bold text-gray-900">{filteredReceipts.length}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Reçus émis</p>
              <p className="text-2xl font-bold text-green-600">{issuedCount}</p>
            </div>
            <FileText className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Montant total</p>
              <p className="text-2xl font-bold text-gray-900">{totalAmount.toFixed(2)} €</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par n° reçu, donateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="issued">Émis</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">Chargement...</div>
        ) : filteredReceipts.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p>Aucun reçu fiscal trouvé</p>
            <p className="text-sm text-gray-500 mt-1">Créez votre premier reçu fiscal pour cette association</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° reçu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Donateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date don
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date émission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReceipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {receipt.receipt_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{receipt.donor_name}</div>
                      {receipt.donor_company_name && (
                        <div className="text-xs text-gray-500">{receipt.donor_company_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(receipt.donation_date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(Number(receipt.donation_amount) + Number(receipt.donation_nature_amount || 0)).toFixed(2)} €
                      </div>
                      {Number(receipt.donation_nature_amount) > 0 && (
                        <div className="text-xs text-gray-500">
                          dont {Number(receipt.donation_nature_amount).toFixed(2)} € en nature
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {receipt.issue_date ? new Date(receipt.issue_date).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(receipt.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedReceipt(receipt);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {receipt.pdf_url && (
                          <button
                            onClick={() => handleDownloadPDF(receipt.id, receipt.pdf_url)}
                            className="text-green-600 hover:text-green-800"
                            title="Télécharger PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDuplicate(receipt)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        {receipt.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancel(receipt.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Annuler"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateTaxReceiptModal
          associationId={associationId}
          associationName={associationName}
          userId={userId}
          existingReceipt={selectedReceipt}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedReceipt(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedReceipt(null);
            loadReceipts();
          }}
        />
      )}

      {showDetailModal && selectedReceipt && (
        <TaxReceiptDetailModal
          receipt={selectedReceipt}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedReceipt(null);
          }}
          onEdit={() => {
            setShowDetailModal(false);
            setShowCreateModal(true);
          }}
        />
      )}
    </div>
  );
}
